/**
 * 대운/세운/월운 관리 컴포저블
 * 
 * 책임:
 * - 대운/세운/월운 데이터 관리
 * - 운 선택 및 적용
 * - 대운 적용 토글
 * - 펼침/접힘 상태 관리
 */

import { ref, reactive } from 'vue';
import { yearMonth, fastSearch } from '@/api/Eoway';
import fiveMixin from '@/composables/fiveMixin';
import { DEV_MODE } from '@/config/dev-mode';

export function useWoonManagement() {
  const { getKorTxt } = fiveMixin();

  // ===== 상태 관리 =====
  
  /** 대운 데이터 */
  const daeunsData = ref({ sky: [], earth: [] });
  /** 세운 데이터 */
  const yearsData = ref({ sky: [], earth: [] });
  /** 월운 데이터 */
  const monthsData = ref({ sky: [], earth: [] });
  
  /** 현재 대운 */
  const curDaeun = ref({ sky: [], earth: [] });
  /** 선택된 대운 */
  const selectDaeun = ref({});
  
  /** 현재 선택된 대운 번호 */
  const daeunCurNum = ref(0);
  /** 현재 선택된 세운 번호 */
  const daeunYearCurNum = ref(0);
  /** 현재 선택된 월운 번호 */
  const daeunMonthCurNum = ref(0);
  /** 세운 인덱스 */
  const seunIndex = ref(0);
  
  /** 대운이 있는지 여부 */
  const isDaeunCheck = ref(false);
  /** 세운 데이터 존재 여부 */
  const isDaeunYear = ref(true);
  /** 월운 데이터 존재 여부 */
  const isDaeunMonth = ref(true);
  
  /** 세운 펼침/접힘 상태 */
  const isSeWoonOpen = ref(true);
  /** 월운 펼침/접힘 상태 */
  const isWolWoonOpen = ref(true);
  
  /** 선택된 대운/세운/월운 텍스트 */
  const selectedBlylml = reactive({
    bl: '', // 대운
    yl: '', // 세운
    ml: '', // 월운
  });
  
  /** 이전 대운/세운/월운 텍스트 */
  const prevBlylml = reactive({
    bl: '',
    yl: '',
    ml: '',
  });
  
  /** 대운 적용 천간 데이터 */
  const hapBlDataSky = ref(null);
  /** 대운 적용 지지 데이터 */
  const hapBlDataEarth = ref(null);
  
  /** 대운 적용 기능 사용 여부 */
  const isapplyWoon = ref(true);
  
  // ===== 내부 상태 (외부에서 주입받음) =====
  let _inputdata = null;
  let _birthData = null;
  let _langMode = null;
  let _formData = null;
  let _searchParams = null;
  let _five = null;

  // ===== 초기화 =====
  
  /**
   * 컴포저블 초기화
   * @param {Object} deps - 외부 의존성
   * @param {Ref} deps.inputdata - 사주 입력 데이터
   * @param {Ref} deps.birthData - 사주 간지 데이터
   * @param {Ref} deps.langMode - 언어 모드
   * @param {Ref} deps.formData - 폼 데이터
   * @param {Ref} deps.searchParams - 사주 조회 파라미터
   * @param {Object} deps.five - 오행 유틸리티
   */
  const initialize = (deps) => {
    _inputdata = deps.inputdata;
    _birthData = deps.birthData;
    _langMode = deps.langMode;
    _formData = deps.formData;
    _searchParams = deps.searchParams;
    _five = deps.five;
  };

  /**
   * 사주 조회 후 대운 데이터 설정
   * @param {Object} sajuResult - 사주 조회 결과
   */
  const setInitialData = (sajuResult) => {
    if (!sajuResult) return;

    daeunsData.value = sajuResult.daeuns || { sky: [], earth: [] };
    yearsData.value = sajuResult.years || { sky: [], earth: [] };
    monthsData.value = sajuResult.months || { sky: [], earth: [] };
    
    const curlucknum = sajuResult.transdata?.curlucknum || 0;
    daeunCurNum.value = curlucknum > 100 
      ? Math.max(...sajuResult.daeuns.sky.map((da) => da.num))
      : curlucknum;
    
    if (sajuResult.selectyear) {
      daeunYearCurNum.value = sajuResult.selectyear;
    }
    if (sajuResult.selectmonth) {
      daeunMonthCurNum.value = sajuResult.selectmonth;
    }
    
    curDaeun.value.sky = sajuResult.daeuns.sky.filter((da) => da.num === daeunCurNum.value);
    curDaeun.value.earth = sajuResult.daeuns.earth.filter((da) => da.num === daeunCurNum.value);
  };

  // ===== 핵심 기능 =====
  
  /**
   * 대운/세운/월운 클릭 처리
   * @param {string} typ - 'daeun' | 'seun' | 'month'
   * @param {Object} item - 선택된 운 데이터
   * @param {Object} curSelectedDaeun - 현재 선택된 대운 전체 데이터
   */
  const getWoon = async (typ, item, curSelectedDaeun = {}) => {
    // 선택된 대운/세운/월운 한글 텍스트 추출 및 업데이트
    let luckSkyValue = curSelectedDaeun?.sky?.find((obj) => obj.num === item.num)?.value || ' ';
    let luckEarthValue = curSelectedDaeun?.earth?.find((obj) => obj.num === item.num)?.value || ' ';
    
    if (_langMode?.value === 'CHN') {
      // 한글 변환
      luckSkyValue = getKorTxt(luckSkyValue, 'sky');
      luckEarthValue = getKorTxt(luckEarthValue, 'earth');
    }
    
    const luckText = luckSkyValue + luckEarthValue;
    updateSelectedWoon({ typ, luckText });

    switch (typ) {
      case 'month':
        daeunMonthCurNum.value = item.num;
        break;
      case 'seun':
        if (prevBlylml.yl !== selectedBlylml.yl) {
          await getMonth(item);
        }
        break;
      case 'daeun':
        // 대운이 클릭되면 세운과 월운이 접혀있을 경우 자동으로 펼침
        if (!isSeWoonOpen.value) isSeWoonOpen.value = true;
        if (!isWolWoonOpen.value) isWolWoonOpen.value = true;
        
        if (prevBlylml.bl !== selectedBlylml.bl) {
          await getSeun(item);
          curDaeun.value.sky = curSelectedDaeun?.sky?.filter((da) => da.num === item.num) || [];
          curDaeun.value.earth = curSelectedDaeun?.earth?.filter((da) => da.num === item.num) || [];
        }
        break;
    }
  };

  /**
   * 세운 데이터 조회
   * @param {Object} item - 대운 아이템
   */
  const getSeun = async (item) => {
    if (!_inputdata?.value || !_birthData?.value || !_formData?.value) {
      console.error('useWoonManagement: Required dependencies not initialized');
      return;
    }

    daeunCurNum.value = item?.num;
    const formParams = {
      birthYear: _inputdata.value.year.toString(),
      daun: daeunCurNum.value.toString(),
      hanja: _birthData.value?.sky?.day?.value,
      seun: 0,
      lang: _formData.value.lang,
    };
    
    selectDaeun.value = item;
    isDaeunYear.value = true;
    
    const result = await yearMonth(formParams);
    
    // 데이터를 한 번에 업데이트하여 깜박임 방지
    yearsData.value = {
      sky: reverseData(result.years.sky),
      earth: reverseData(result.years.earth),
    };
    
    // 월운 호출
    await getMonth(result.years.sky[0]);

    // 현재 연도가 있으면 현재 연도로, 없으면 마지막 항목으로 설정
    const currYear = new Date().getFullYear();
    const foundYearData = yearsData.value.sky.find((item) => item.num === currYear);
    daeunYearCurNum.value = foundYearData ? foundYearData.num : yearsData.value.sky[yearsData.value.sky.length - 1].num;
  };

  /**
   * 월운 데이터 조회
   * @param {Object} item - 세운 아이템
   */
  const getMonth = async (item) => {
    if (!_inputdata?.value || !_birthData?.value || !_formData?.value) {
      console.error('useWoonManagement: Required dependencies not initialized');
      return;
    }

    seunIndex.value = item?.num;
    const formParams = {
      birthYear: _inputdata.value.year,
      daun: `${daeunCurNum.value}`,
      hanja: _birthData.value?.sky?.day?.value,
      seun: `${item?.num}`,
      lang: _formData.value.lang,
    };
    
    isDaeunMonth.value = true;
    daeunYearCurNum.value = item?.num;
    
    const result = await yearMonth(formParams);
    
    // 데이터를 한 번에 업데이트하여 깜박임 방지
    monthsData.value = {
      sky: reverseData(result.months.sky),
      earth: reverseData(result.months.earth),
    };
    
    daeunMonthCurNum.value = monthsData.value.sky[monthsData.value.sky.length - 1].num;
  };

  /**
   * 선택된 대운/세운/월운 업데이트
   * @param {Object} params
   * @param {string} params.typ - 'daeun' | 'seun' | 'month'
   * @param {string} params.luckText - 운 텍스트
   */
  const updateSelectedWoon = ({ typ, luckText }) => {
    if (typ === 'daeun') {
      prevBlylml.bl = selectedBlylml.bl;
      selectedBlylml.bl = luckText;
    } else if (typ === 'seun') {
      prevBlylml.yl = selectedBlylml.yl;
      selectedBlylml.yl = luckText;
    } else if (typ === 'month') {
      prevBlylml.ml = selectedBlylml.ml;
      selectedBlylml.ml = luckText;
    }
  };

  /**
   * 대운 적용 토글
   */
  const clickDaeunCheck = async () => {
    isDaeunCheck.value = !isDaeunCheck.value;
    if (isDaeunCheck.value) {
      await applyWoon({ flag: true });
    }
  };

  /**
   * 대운 적용 API 호출
   * @param {Object} params
   * @param {boolean} params.flag - 적용 플래그
   * @param {boolean} params.isLangChanged - 언어 변경 여부
   */
  const applyWoon = async ({ flag, isLangChanged = false }) => {
    if (!isapplyWoon.value || !_searchParams?.value || !_five) return;
    
    const blylml = selectedBlylml.bl + selectedBlylml.yl + selectedBlylml.ml;
    const isBlChanged = prevBlylml.bl !== selectedBlylml.bl;

    if (isDaeunCheck.value && ((flag && isBlChanged) || isLangChanged)) {
      try {
        DEV_MODE && console.log('applyWoon blylml:', blylml);

        const params = {
          ..._searchParams.value,
          blCheck: 'Y',
          blylml,
        };

        const data = await fastSearch(params);

        prevBlylml.bl = selectedBlylml.bl;

        if (data) {
          hapBlDataSky.value = _five
            .convertHap({ hap: data?.hap_bl, isSky: true, langMode: _langMode?.value })
            .join('ㆍ');
          hapBlDataEarth.value = _five
            .convertHap({ hap: data?.hap_bl, isSky: false, langMode: _langMode?.value })
            .join('ㆍ');
        } else {
          hapBlDataSky.value = null;
          hapBlDataEarth.value = null;
        }
      } catch (e) {
        DEV_MODE && console.log('manse applyWoon error:', e);
      }
    }
  };

  /**
   * 대운 데이터 역순 정렬
   */
  const reverseDaeunData = () => {
    daeunsData.value.sky = reverseData(daeunsData.value.sky);
    daeunsData.value.earth = reverseData(daeunsData.value.earth);
    monthsData.value.sky = reverseData(monthsData.value.sky);
    monthsData.value.earth = reverseData(monthsData.value.earth);
    yearsData.value.sky = reverseData(yearsData.value.sky);
    yearsData.value.earth = reverseData(yearsData.value.earth);

    // 순서만 변경하고, 현재 선택된 세운/월운은 그대로 유지
  };

  /**
   * 세운 펼침/접힘 토글
   */
  const toggleSeWoon = () => {
    isSeWoonOpen.value = !isSeWoonOpen.value;
  };

  /**
   * 월운 펼침/접힘 토글
   */
  const toggleWolWoon = () => {
    isWolWoonOpen.value = !isWolWoonOpen.value;
  };

  // ===== 유틸리티 =====
  
  /**
   * 배열 데이터 역순 정렬
   * @param {Array} data - 정렬할 데이터
   * @returns {Array} 역순 정렬된 데이터
   */
  const reverseData = (data) => {
    return Object.values(data).reverse();
  };

  // ===== 반환 =====
  
  return {
    // 상태
    daeunsData,
    yearsData,
    monthsData,
    curDaeun,
    selectDaeun,
    daeunCurNum,
    daeunYearCurNum,
    daeunMonthCurNum,
    seunIndex,
    isDaeunCheck,
    isDaeunYear,
    isDaeunMonth,
    isSeWoonOpen,
    isWolWoonOpen,
    selectedBlylml,
    prevBlylml,
    hapBlDataSky,
    hapBlDataEarth,
    isapplyWoon,
    
    // 메서드
    initialize,
    setInitialData,
    getWoon,
    getSeun,
    getMonth,
    updateSelectedWoon,
    clickDaeunCheck,
    applyWoon,
    reverseDaeunData,
    toggleSeWoon,
    toggleWolWoon,
  };
}
